import { useEffect, useRef } from "react";
import { siteConfig } from "@/config";

declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

interface AdUnitProps {
  slot: string;
  variant?: "display" | "inarticle" | "multiplex" | "infeed";
  minHeight?: number;
  className?: string;
  layoutKey?: string;
}

export function AdUnit({
  slot,
  variant = "display",
  minHeight = 120,
  className,
  layoutKey,
}: AdUnitProps) {
  const insRef = useRef<HTMLModElement | null>(null);
  const pushed = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (pushed.current) return;
    if (!slot) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      pushed.current = true;
    } catch {
      /* no-op */
    }
  }, [slot]);

  if (!slot) return null;

  const style: React.CSSProperties = {
    display: "block",
    minHeight,
    textAlign: "center",
  };

  const commonProps = {
    ref: insRef,
    className: "adsbygoogle",
    style,
    "data-ad-client": siteConfig.adsensePublisherId,
    "data-ad-slot": slot,
  } as const;

  return (
    <div className={className} aria-hidden="true">
      {variant === "inarticle" ? (
        <ins
          {...commonProps}
          data-ad-layout="in-article"
          data-ad-format="fluid"
        />
      ) : variant === "multiplex" ? (
        <ins {...commonProps} data-ad-format="autorelaxed" />
      ) : variant === "infeed" ? (
        <ins
          {...commonProps}
          data-ad-format="fluid"
          data-ad-layout-key={layoutKey ?? siteConfig.adsenseInFeedLayoutKey}
        />
      ) : (
        <ins {...commonProps} data-ad-format="auto" data-full-width-responsive="true" />
      )}
    </div>
  );
}

export function InFeedAdCard() {
  const slot = siteConfig.adSlots.homeInFeed;
  if (!slot) return null;
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <AdUnit slot={slot} variant="infeed" minHeight={280} />
    </div>
  );
}
